import { Dialog, DialogTitle, DialogContent, Stack, Button } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SignatureCanvas from 'react-signature-canvas';
import { useSnackbar } from 'notistack';

import { useFirmarMutation } from '../hooks/useFirmas';

interface Props {
  open: boolean;
  reservaId: number;
  onClose: () => void;
}

export default function FirmaDialog({ open, reservaId, onClose }: Props) {
  const { t } = useTranslation<'translation'>();
  const { enqueueSnackbar } = useSnackbar(); // Añadir snackbar para feedback

  const sigRef = useRef<SignatureCanvas>(null);
  const { mutateAsync: firmar, isPending: firmaLoading } = useFirmarMutation();

  // Estado de la firma (para habilitar/deshabilitar el botón Guardar)
  const [hasSignature, setHasSignature] = useState(false);
  const updateHasSignature = () => setHasSignature(!(sigRef.current?.isEmpty() ?? true));

  // Al abrir el diálogo (o cambiar de reserva), limpiar el canvas
  useEffect(() => {
    if (!open) return;
    try {
      sigRef.current?.clear();
    } catch {
      /* noop */
    }
    setHasSignature(false);
  }, [open, reservaId]);

  const getTrimmedCanvas = () => {
    if (!sigRef.current) return null;

    const originalCanvas = sigRef.current.getCanvas();
    const ctx = originalCanvas.getContext('2d');
    if (!ctx) return null;

    // Obtener los datos de la imagen
    const imageData = ctx.getImageData(0, 0, originalCanvas.width, originalCanvas.height);
    const { data, width, height } = imageData;

    // Encontrar los bordes con contenido
    let top = height;
    let bottom = 0;
    let left = width;
    let right = 0;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alpha = data[(y * width + x) * 4 + 3];
        if (alpha > 0) {
          top = Math.min(top, y);
          bottom = Math.max(bottom, y);
          left = Math.min(left, x);
          right = Math.max(right, x);
        }
      }
    }

    // Si no hay contenido, devolver null
    if (top > bottom || left > right) {
      return null;
    }

    // Añadir un pequeño margen
    const margin = 10;
    top = Math.max(0, top - margin);
    bottom = Math.min(height, bottom + margin);
    left = Math.max(0, left - margin);
    right = Math.min(width, right + margin);

    // Crear un nuevo canvas con el área recortada
    const trimmedCanvas = document.createElement('canvas');
    trimmedCanvas.width = right - left;
    trimmedCanvas.height = bottom - top;

    const trimmedCtx = trimmedCanvas.getContext('2d');
    if (!trimmedCtx) return null;

    trimmedCtx.drawImage(
      originalCanvas,
      left,
      top,
      right - left,
      bottom - top,
      0,
      0,
      right - left,
      bottom - top,
    );

    return trimmedCanvas;
  };

  const guardarFirma = async () => {
    if (!sigRef.current || !hasSignature) {
      enqueueSnackbar(t('firmaDialog.noSignatureError'), { variant: 'warning' });
      return;
    }

    try {
      // Usar el canvas recortado en lugar del original
      const trimmedCanvas = getTrimmedCanvas();

      if (!trimmedCanvas) {
        enqueueSnackbar(t('firmaDialog.emptySignatureError'), { variant: 'error' });
        return;
      }

      // Convertir el canvas recortado a base64
      const png = trimmedCanvas.toDataURL('image/png').split(',')[1];

      if (!png) {
        throw new Error('Error al generar la imagen de la firma');
      }

      await firmar({ id: reservaId, pngBase64: png });
      window.dispatchEvent(new CustomEvent('firmas:signed'));
      onClose();
    } catch (error: any) {
      // Mostrar error específico si está disponible
      const errorMessage = error?.message || t('firmaDialog.saveError');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('firmaDialog.title')}</DialogTitle>

      <DialogContent dividers sx={{ p: 2, position: 'relative' }}>
        {/* Instrucción para dibujar dentro del recuadro */}
        <div
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            right: 10,
            textAlign: 'center',
            fontSize: '12px',
            color: '#666',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          {t('firmaDialog.instructions')}
        </div>

        {/* Canvas de firma con contenedor que previene desbordamiento */}
        <div
          style={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 4,
            border: '1px solid #ccc',
            marginTop: 20,
          }}
        >
          <SignatureCanvas
            ref={sigRef}
            penColor="black"
            onEnd={updateHasSignature}
            canvasProps={{
              width: 400,
              height: 200,
              style: {
                display: 'block',
                cursor: 'crosshair',
              },
            }}
          />
        </div>

        <Stack direction="row" spacing={2} mt={2} justifyContent="flex-end">
          <Button
            onClick={() => {
              sigRef.current?.clear();
              setHasSignature(false);
            }}
          >
            {t('common.clear')}
          </Button>
          <Button
            variant="contained"
            onClick={guardarFirma}
            disabled={firmaLoading || !hasSignature}
            sx={{
              backgroundColor: '#21a598',
              '&:hover': { backgroundColor: '#1a8578' },
            }}
          >
            {firmaLoading ? t('common.saving') : t('common.save')}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
